import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CloseIcon from '@mui/icons-material/Close';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PageHeader from '../components/PageHeader';
import EmptyState from '../components/EmptyState';
import LoadingFallback from '../components/LoadingFallback';
import StatusChip from '../components/StatusChip';
import InviteDialog from '../components/InviteDialog';
import { api, apiErrorMessage } from '../api/client';
import { useFeedback } from '../contexts/FeedbackContext';
import type { Invite, User } from '../api/types';
import dayjs from 'dayjs';

export default function CoachesPage() {
  const qc = useQueryClient();
  const { confirm, toast } = useFeedback();
  const [tab, setTab] = useState(0);
  const [inviteOpen, setInviteOpen] = useState(false);

  const usersQuery = useQuery<User[]>({
    queryKey: ['users', 'coaches'],
    queryFn: async () => (await api.get('/users', { params: { role: 'coach' } })).data,
  });
  const adminsQuery = useQuery<User[]>({
    queryKey: ['users', 'admins'],
    queryFn: async () => (await api.get('/users', { params: { role: 'admin' } })).data,
  });
  const invitesQuery = useQuery<Invite[]>({
    queryKey: ['invites'],
    queryFn: async () => (await api.get('/invites')).data,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['users'] });
    qc.invalidateQueries({ queryKey: ['invites'] });
  };

  const deactivate = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/users/${id}`)).data,
    onSuccess: () => {
      toast({ message: 'User deactivated', severity: 'success' });
      refresh();
    },
    onError: (e) => toast({ message: apiErrorMessage(e), severity: 'error' }),
  });

  const resend = useMutation({
    mutationFn: async (id: number) => (await api.post(`/invites/${id}/resend`)).data,
    onSuccess: () => {
      toast({ message: 'Invite resent', severity: 'success' });
      refresh();
    },
    onError: (e) => toast({ message: apiErrorMessage(e), severity: 'error' }),
  });

  const revoke = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/invites/${id}`)).data,
    onSuccess: () => {
      toast({ message: 'Invite revoked', severity: 'info' });
      refresh();
    },
    onError: (e) => toast({ message: apiErrorMessage(e), severity: 'error' }),
  });

  return (
    <Box>
      <PageHeader
        title="Coaches & admins"
        subtitle="Invite team members and manage who can run meets. Admins can also be assigned as athlete coaches."
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setInviteOpen(true)}
            aria-label="Invite teammate"
          >
            Invite
          </Button>
        }
      />

      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
          <Tab label="Coaches" id="tab-coaches" aria-controls="panel-coaches" />
          <Tab label="Admins" id="tab-admins" aria-controls="panel-admins" />
          <Tab label="Pending invites" id="tab-invites" aria-controls="panel-invites" />
        </Tabs>

        {tab === 0 && (
          <CardContent>
            <UserTable
              loading={usersQuery.isLoading}
              users={usersQuery.data}
              onDeactivate={(u) => confirmDeactivate(confirm, deactivate.mutate, u)}
            />
          </CardContent>
        )}
        {tab === 1 && (
          <CardContent>
            <UserTable
              loading={adminsQuery.isLoading}
              users={adminsQuery.data}
              onDeactivate={(u) => confirmDeactivate(confirm, deactivate.mutate, u)}
            />
          </CardContent>
        )}
        {tab === 2 && (
          <CardContent>
            {invitesQuery.isLoading ? (
              <LoadingFallback />
            ) : (invitesQuery.data ?? []).length === 0 ? (
              <EmptyState title="No invites yet" description="Send your first invite to bring teammates in." />
            ) : (
              <Table size="small" aria-label="Pending invites">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(invitesQuery.data ?? []).map((inv) => (
                    <TableRow key={inv.id} hover>
                      <TableCell>{inv.email}</TableCell>
                      <TableCell><Chip label={inv.role} size="small" /></TableCell>
                      <TableCell><StatusChip status={inv.status} /></TableCell>
                      <TableCell>{dayjs(inv.expires_at).format('MMM D, YYYY')}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Resend invite">
                          <span>
                            <IconButton
                              aria-label={`Resend invite to ${inv.email}`}
                              onClick={() => resend.mutate(inv.id)}
                              disabled={inv.status !== 'pending'}
                            >
                              <RestartAltIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Revoke invite">
                          <span>
                            <IconButton
                              aria-label={`Revoke invite to ${inv.email}`}
                              onClick={async () => {
                                const ok = await confirm({
                                  title: 'Revoke invite?',
                                  message: `Revoke the invite to ${inv.email}?`,
                                  destructive: true,
                                });
                                if (ok) revoke.mutate(inv.id);
                              }}
                              disabled={inv.status !== 'pending'}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        )}
      </Card>

      <InviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvited={refresh}
        defaultRole="coach"
        roles={['admin', 'coach']}
      />
    </Box>
  );
}

function confirmDeactivate(
  confirm: (opts: { title?: string; message: string; destructive?: boolean; confirmLabel?: string }) => Promise<boolean>,
  deactivate: (id: number) => void,
  u: User,
) {
  confirm({
    title: 'Deactivate user?',
    message: `${u.full_name || u.email} will no longer be able to log in.`,
    destructive: true,
    confirmLabel: 'Deactivate',
  }).then((ok) => {
    if (ok) deactivate(u.id);
  });
}

function UserTable({
  loading,
  users,
  onDeactivate,
}: {
  loading: boolean;
  users?: User[];
  onDeactivate: (u: User) => void;
}) {
  if (loading) return <LoadingFallback />;
  if (!users || users.length === 0) {
    return <EmptyState title="No users yet" description="Send invites above to get started." />;
  }
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Name</TableCell>
          <TableCell>Email</TableCell>
          <TableCell>Phone</TableCell>
          <TableCell>Status</TableCell>
          <TableCell align="right" />
        </TableRow>
      </TableHead>
      <TableBody>
        {users.map((u) => (
          <TableRow key={u.id} hover>
            <TableCell>{u.full_name || '—'}</TableCell>
            <TableCell>{u.email}</TableCell>
            <TableCell>{u.phone || '—'}</TableCell>
            <TableCell>
              <Stack direction="row" spacing={1}>
                <Chip
                  size="small"
                  color={u.is_active ? 'success' : 'default'}
                  label={u.is_active ? 'Active' : 'Inactive'}
                  variant={u.is_active ? 'filled' : 'outlined'}
                />
                <Chip size="small" label={u.role} />
              </Stack>
            </TableCell>
            <TableCell align="right">
              {u.is_active && (
                <Tooltip title="Deactivate">
                  <IconButton aria-label={`Deactivate ${u.full_name || u.email}`} onClick={() => onDeactivate(u)}>
                    <PersonOffIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
